import { useMemo, useRef } from 'react';
import { createStore, applyMiddleware } from 'redux';
import { StateType } from 'typesafe-actions';
import { useNavigate } from 'react-router-dom';

import { RootAction } from 'store/actions';
import { RootDependencies, NavigationService } from 'store/dependencies';
import { composeEnhancers } from 'store/utils';
import { createEpicMiddleware } from 'redux-observable';
import { apiClient } from 'store/api-client';
import rootEpic from './epics';
import rootReducer, { RootState } from './reducer';
import { checkTokenExpirationMiddleware } from 'store/middlewares/check-token-expiration';
import { saveFiltersMiddleware } from 'store/middlewares/save-filters';
import { createReduxEnhancer } from '@sentry/react';
import { sentryStateTransformer } from './utils/sentry-state-transformer.util';

const createNavigationService = (navigate: ReturnType<typeof useNavigate>): NavigationService => ({
  push: (path: string) => navigate(path),
  replace: (path: string) => navigate(path, { replace: true }),
});

const createReduxStore = (navigate: ReturnType<typeof useNavigate>) => {
  const sentryReduxEnhancer = createReduxEnhancer({
    stateTransformer: sentryStateTransformer,
  });

  const navigation = createNavigationService(navigate);

  const epicMiddleware = createEpicMiddleware<RootAction, RootAction, RootState, RootDependencies>({
    dependencies: {
      apiClient,
      navigation,
    },
  });

  const enhancer = composeEnhancers(
    applyMiddleware(checkTokenExpirationMiddleware, saveFiltersMiddleware, epicMiddleware),
    sentryReduxEnhancer,
  );
  const initialState = {};
  const store = createStore(rootReducer, initialState, enhancer);

  epicMiddleware.run(rootEpic);

  return store;
};

export const useStore = () => {
  const navigate = useNavigate();
  // Use a ref to ensure store is only created once
  // In React Router v6, navigate is stable, but this ensures we only create store once
  const storeRef = useRef<ReturnType<typeof createReduxStore> | null>(null);

  // Create store only once - navigate function is stable in React Router v6
  const reduxStore = useMemo(() => {
    if (!storeRef.current) {
      storeRef.current = createReduxStore(navigate);
    }
    return storeRef.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - store should only be created once

  return reduxStore;
};

export type Store = StateType<ReturnType<typeof useStore>>;
